/* ═══════════════════════════════════════════════════════════════════════
   RARE LEATHERS — THE PRIVATE ROOM
   JS natif, aucune dependance. Le thème reste maitre du panier : on poste
   sur /cart/add.js puis on emet l'evenement 'cart:update' du thème, que la
   pastille du header et le tiroir panier ecoutent deja.
   ═══════════════════════════════════════════════════════════════════════ */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
const FADE_MS = 250;
const ADDED_MS = 1500;
const SWIPE_PX = 40;

/* ───── Hauteur du header ─────
   On ne fait confiance a aucune variable du thème : --header-height ne
   compte pas la barre d'annonce et vaut 37 px alors que l'en-tete complet
   en fait 160. On mesure donc ce qui occupe reellement le haut de la page,
   c'est-a-dire la distance entre le haut du document et notre section.
   Resultat ecrit sur :root, comme demande. */
function watchHeaderHeight(host) {
  if (!host) return;

  const apply = () => {
    const top = Math.round(host.getBoundingClientRect().top + window.scrollY);
    if (top > 0 && top < window.innerHeight) {
      document.documentElement.style.setProperty('--rl-header-h', top + 'px');
    }
  };

  apply();
  requestAnimationFrame(apply);
  window.addEventListener('load', apply);
  window.addEventListener('orientationchange', apply);
  window.addEventListener('resize', apply, { passive: true });

  const header =
    document.querySelector('.shopify-section-group-header-group') ||
    document.querySelector('#header-component') ||
    document.querySelector('header');
  if (header) new ResizeObserver(apply).observe(header);
}

class RareLeathersStage extends HTMLElement {
  connectedCallback() {
    /* La mesure du header commande la hauteur de la scene : elle doit se
       faire meme si les donnees produit manquent, sinon le socle sort de
       l'ecran. */
    watchHeaderHeight(this);

    const raw = this.querySelector('[data-rl-products]');
    try {
      this.items = JSON.parse(raw ? raw.textContent : '[]');
    } catch {
      this.items = [];
    }
    if (!this.items.length) return;

    this.index = 0;
    this.view = 0;
    this.openDrawer = this.dataset.openDrawer === 'true';
    this.showQuickAdd = this.dataset.showQuickAdd === 'true';
    this.labelAdd = this.dataset.labelAdd || 'Add to bag';
    this.labelAdded = this.dataset.labelAdded || 'Added';
    this.labelSoldOut = this.dataset.labelSoldOut || 'Ask for the next';
    this.soldOutUrl = this.dataset.soldOutUrl || '';

    this.el = {
      stage: this.querySelector('[data-rl-stage]'),
      wrap: this.querySelector('[data-rl-piece-wrap]'),
      piece: this.querySelector('[data-rl-piece]'),
      quickAdd: this.querySelector('[data-rl-quick-add]'),
      counter: this.querySelector('[data-rl-counter]'),
      badge: this.querySelector('[data-rl-badge]'),
      name: this.querySelector('[data-rl-name]'),
      leather: this.querySelector('[data-rl-leather]'),
      price: this.querySelector('[data-rl-price]'),
      cta: this.querySelector('[data-rl-cta]'),
      error: this.querySelector('[data-rl-error]'),
      discover: this.querySelector('[data-rl-discover]'),
      thumbs: Array.from(this.querySelectorAll('[data-rl-thumb]')),
      views: this.querySelector('[data-rl-views]'),
      viewPrev: this.querySelector('[data-rl-view-prev]'),
      viewNext: this.querySelector('[data-rl-view-next]'),
    };

    this.bind();
    this.preloadNeighbours();
  }

  bind() {
    this.el.thumbs.forEach((btn, i) => {
      btn.addEventListener('click', () => this.go(i));
    });

    const prev = this.querySelector('[data-rl-prev]');
    const next = this.querySelector('[data-rl-next]');
    if (prev) prev.addEventListener('click', () => this.go(this.index - 1));
    if (next) next.addEventListener('click', () => this.go(this.index + 1));

    if (this.el.stage) {
      /* Gauche et droite changent de piece ; haut et bas tournent la piece
         en cours. Deux axes, deux gestes. */
      this.el.stage.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.go(this.index - 1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.go(this.index + 1);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          this.goView(this.view - 1);
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          this.goView(this.view + 1);
        }
      });
      this.bindSwipe(this.el.stage);
    }

    if (this.el.viewPrev) this.el.viewPrev.addEventListener('click', () => this.goView(this.view - 1));
    if (this.el.viewNext) this.el.viewNext.addEventListener('click', () => this.goView(this.view + 1));

    if (this.el.cta) this.el.cta.addEventListener('click', (e) => this.onCta(e));
    if (this.el.quickAdd) this.el.quickAdd.addEventListener('click', () => this.add());
  }

  /* Le swipe ne doit jamais bloquer le defilement vertical : on ne decide
     qu'une fois le geste clairement horizontal. */
  bindSwipe(target) {
    let x0 = null;
    let y0 = null;

    target.addEventListener(
      'touchstart',
      (e) => {
        const t = e.changedTouches[0];
        x0 = t.clientX;
        y0 = t.clientY;
      },
      { passive: true }
    );

    target.addEventListener(
      'touchend',
      (e) => {
        if (x0 === null) return;
        const t = e.changedTouches[0];
        const dx = t.clientX - x0;
        const dy = t.clientY - y0;
        x0 = null;
        if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) <= Math.abs(dy)) return;
        this.go(dx < 0 ? this.index + 1 : this.index - 1);
      },
      { passive: true }
    );
  }

  go(i) {
    const n = this.items.length;
    const next = ((i % n) + n) % n;
    if (next === this.index) return;
    this.index = next;
    this.view = 0;
    this.fade();
  }

  /* Changer d'angle sur la piece en cours : meme fondu, le socle ne bouge
     pas et le panneau produit non plus. */
  goView(i) {
    const views = this.views();
    if (views.length < 2) return;
    const next = ((i % views.length) + views.length) % views.length;
    if (next === this.view) return;
    this.view = next;
    this.fade();
  }

  views() {
    const it = this.items[this.index];
    return (it && Array.isArray(it.views) && it.views.length) ? it.views : [];
  }

  fade() {
    if (REDUCED_MOTION.matches) {
      this.render();
      return;
    }
    this.el.wrap.dataset.fading = 'true';
    clearTimeout(this.fadeTimer);
    this.fadeTimer = setTimeout(() => {
      this.render();
      requestAnimationFrame(() => {
        this.el.wrap.dataset.fading = 'false';
      });
    }, FADE_MS);
  }

  render() {
    const it = this.items[this.index];
    const e = this.el;

    const views = this.views();
    const v = views[this.view] || views[0] || {};
    e.piece.src = v.img || '';
    if (v.srcset) {
      e.piece.srcset = v.srcset;
    } else {
      e.piece.removeAttribute('srcset');
    }
    e.piece.alt = it.alt || '';
    e.piece.classList.toggle('rl-piece--fallback', !!it.fallback);
    e.wrap.style.setProperty('--scale', it.scale);

    if (e.counter) {
      e.counter.textContent =
        String(this.index + 1).padStart(2, '0') + ' / ' + String(this.items.length).padStart(2, '0');
    }

    if (e.badge) e.badge.textContent = it.badge || '';
    if (e.name) e.name.textContent = it.name;
    if (e.leather) e.leather.textContent = it.leather || '';
    if (e.price) e.price.textContent = it.price;
    if (e.discover) e.discover.href = it.url;
    if (e.error) e.error.textContent = '';

    this.renderCta(it);
    this.renderQuickAdd(it);
    this.renderViews();

    e.thumbs.forEach((btn, i) => btn.setAttribute('aria-pressed', String(i === this.index)));
    const active = e.thumbs[this.index];
    if (active && active.scrollIntoView) {
      active.scrollIntoView({ block: 'nearest', inline: 'center', behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' });
    }

    this.preloadNeighbours();
  }

  /* Le bouton est un <button> quand il ajoute au panier, un <a> quand la
     piece est partie. On ne remplace pas le noeud : on bascule son role. */
  renderCta(it) {
    const cta = this.el.cta;
    if (!cta) return;

    if (it.available) {
      cta.textContent = this.labelAdd;
      cta.removeAttribute('href');
      cta.removeAttribute('role');
      cta.disabled = false;
      cta.setAttribute('aria-label', this.labelAdd + ' — ' + it.name + ', ' + (it.leather || ''));
    } else {
      cta.textContent = this.labelSoldOut;
      cta.disabled = false;
      cta.setAttribute('aria-label', this.labelSoldOut + ' — ' + it.name);
      if (this.soldOutUrl) {
        cta.dataset.href = this.soldOutUrl;
      }
    }
    cta.dataset.available = String(!!it.available);
    cta.removeAttribute('aria-busy');
  }

  /* Les pastilles sont reconstruites a chaque changement de piece : le
     nombre d'angles varie d'un sac a l'autre. Masquees sous deux vues. */
  renderViews() {
    const views = this.views();
    const many = views.length > 1;

    if (this.el.viewPrev) this.el.viewPrev.hidden = !many;
    if (this.el.viewNext) this.el.viewNext.hidden = !many;

    const box = this.el.views;
    if (!box) return;

    if (!many || box.hasAttribute('data-rl-views-off')) {
      box.hidden = true;
      box.replaceChildren();
      return;
    }

    box.hidden = false;
    if (box.children.length !== views.length) {
      box.replaceChildren(
        ...views.map((_, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'rl-view-dot';
          b.setAttribute('aria-label', (this.dataset.labelView || 'Vue') + ' ' + (i + 1));
          b.addEventListener('click', () => this.goView(i));
          return b;
        })
      );
    }
    Array.from(box.children).forEach((b, i) =>
      b.setAttribute('aria-pressed', String(i === this.view))
    );
  }

  renderQuickAdd(it) {
    const q = this.el.quickAdd;
    if (!q) return;
    const visible = this.showQuickAdd && it.available;
    q.hidden = !visible;
    if (visible) {
      q.setAttribute('aria-label', this.labelAdd + ' — ' + it.name + ', ' + (it.leather || ''));
    }
  }

  preloadNeighbours() {
    const n = this.items.length;
    [this.index - 1, this.index + 1].forEach((i) => {
      const it = this.items[((i % n) + n) % n];
      if (!it || !it.img) return;
      const img = new Image();
      if (it.srcset) img.srcset = it.srcset;
      img.src = it.img;
    });
  }

  onCta(event) {
    const it = this.items[this.index];
    if (!it.available) {
      if (this.soldOutUrl) window.location.href = this.soldOutUrl;
      return;
    }
    event.preventDefault();
    this.add();
  }

  async add() {
    const it = this.items[this.index];
    const cta = this.el.cta;
    if (!it || !it.available || this.busy) return;
    this.busy = true;

    if (cta) {
      cta.setAttribute('aria-busy', 'true');
      cta.disabled = true;
    }
    if (this.el.error) this.el.error.textContent = '';

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((n) => n.dataset.sectionId)
      .filter(Boolean);

    const body = new FormData();
    body.append('id', it.variantId);
    body.append('quantity', '1');
    if (sectionIds.length) body.append('sections', sectionIds.join(','));

    const url =
      (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await res.json();

      if (!res.ok || data.status) {
        this.fail(data.description || data.message || 'This piece could not be added.');
        return;
      }

      /* On rejoue exactement l'evenement du thème : la pastille du header
         ajoute 1 grace a source: 'product-form-component', et le tiroir
         s'ouvre s'il porte l'attribut auto-open. */
      const ev = new Event('cart:update', { bubbles: true });
      ev.detail = {
        resource: {},
        sourceId: String(it.variantId),
        data: {
          source: 'product-form-component',
          itemCount: 1,
          productId: String(it.id),
          variantId: String(it.variantId),
          sections: data.sections,
        },
      };
      this.dispatchEvent(ev);

      if (this.openDrawer) {
        const drawer = document.querySelector('cart-drawer-component, cart-drawer');
        if (drawer && typeof drawer.open === 'function') drawer.open();
      }

      if (cta) {
        cta.textContent = this.labelAdded;
        clearTimeout(this.addedTimer);
        this.addedTimer = setTimeout(() => this.renderCta(this.items[this.index]), ADDED_MS);
      }
    } catch {
      this.fail('Network error. Please try again.');
    } finally {
      this.busy = false;
      if (cta) {
        cta.removeAttribute('aria-busy');
        cta.disabled = false;
      }
    }
  }

  fail(message) {
    if (this.el.error) this.el.error.textContent = message;
    if (this.el.cta) this.renderCta(this.items[this.index]);
  }
}

if (!customElements.get('rare-leathers-stage')) {
  customElements.define('rare-leathers-stage', RareLeathersStage);
}
